/**
 * Tazminat hesap motoru — İş Gücü Kaybı & Destek Tazminatı
 * Kaynak mantık: Progresif Rant / Aktüeryal (artış≈iskonto iken PV = yıllık × yıl),
 * TRH-2010 bakiye ömür, AYİM paylaştırma (2-2-1), geçmiş dönem gün × (aylık/30).
 *
 * Örnek doğrulama (PDF Aktüeryal):
 * ücret 1003.09, 487 gün → 16.285,28 TL
 * 24,75 yıl × 12.037,08 → 297.917,73 TL
 * 13,5 yıl × 10.932 → 147.582 TL
 */

import { getBakiyeOmur } from './trh2010';
import { getAsgariNet, asgariDonemDilimleri, getGuncelAsgariNet } from './asgariUcret';

const MS_DAY = 24 * 60 * 60 * 1000;
const DAYS_PER_YEAR = 365.25;

export function parseDate(value) {
  if (!value) return null;
  // YYYY-MM-DD'yi yerel gün olarak al (UTC kayması olmasın)
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value)) {
    const [y, m, d] = value.slice(0, 10).split('-').map(Number);
    return new Date(y, m - 1, d);
  }
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

/**
 * @param {boolean} inclusive - PDF örneği gibi her iki uç gün dahil (01.01.2014–02.05.2015 = 487)
 */
export function daysBetween(from, to, inclusive = false) {
  const a = parseDate(from);
  const b = parseDate(to);
  if (!a || !b) return 0;
  const raw = Math.round((b.getTime() - a.getTime()) / MS_DAY);
  if (raw < 0) return 0;
  return inclusive ? raw + 1 : raw;
}

export function yearsBetween(from, to) {
  return daysBetween(from, to, false) / DAYS_PER_YEAR;
}

export function ageAtDate(birthDate, atDate) {
  return yearsBetween(birthDate, atDate);
}

export function addYears(date, years) {
  const d = parseDate(date);
  if (!d) return null;
  const out = new Date(d.getTime());
  const whole = Math.floor(years);
  const frac = years - whole;
  out.setFullYear(out.getFullYear() + whole);
  out.setTime(out.getTime() + frac * DAYS_PER_YEAR * MS_DAY);
  return out;
}

export function addDays(date, days) {
  const d = parseDate(date);
  if (!d) return null;
  const out = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  out.setDate(out.getDate() + (Number(days) || 0));
  return out;
}

/** Takvim yılı dilimleri [start, end] (her iki uç dahil). */
function calendarYearSegments(startDate, endDate) {
  const start = parseDate(startDate);
  const end = parseDate(endDate);
  if (!start || !end || end.getTime() < start.getTime()) return [];
  const segs = [];
  let cur = new Date(start.getFullYear(), start.getMonth(), start.getDate());
  while (cur.getTime() <= end.getTime()) {
    const yearEnd = new Date(cur.getFullYear(), 11, 31);
    const segEnd = yearEnd.getTime() <= end.getTime() ? yearEnd : new Date(end.getFullYear(), end.getMonth(), end.getDate());
    const gun = daysBetween(cur, segEnd, true);
    const ay = gun / 30; // bilirkişi tablolarında ay ≈ gün/30
    segs.push({
      start: new Date(cur.getTime()),
      end: segEnd,
      year: cur.getFullYear(),
      gun,
      ay,
    });
    cur = new Date(segEnd.getFullYear(), segEnd.getMonth(), segEnd.getDate() + 1);
  }
  return segs;
}

/** AYİM dul eş evlenme ihtimali (çocuksuz, kadın). Her <18 çocuk için −5 puan. */
export const AYIM_EVLENME = [
  { maxAge: 20, rate: 52 },
  { maxAge: 25, rate: 40 },
  { maxAge: 30, rate: 28 },
  { maxAge: 34, rate: 17 },
  { maxAge: 39, rate: 10 },
  { maxAge: 44, rate: 5 },
  { maxAge: 50, rate: 2 },
  { maxAge: 200, rate: 0 },
];

export function ayimEvlenmeIhtimali(raporYas, cocukSayisi18alti = 0) {
  const age = Number(raporYas) || 0;
  const row = AYIM_EVLENME.find((r) => age <= r.maxAge) || AYIM_EVLENME[AYIM_EVLENME.length - 1];
  return Math.max(0, row.rate - 5 * Math.max(0, Number(cocukSayisi18alti) || 0));
}

/**
 * Artış/iskonto eşitse faktör = n yıl (PDF: 24,75 × yıllık = bugünkü değer).
 * Değilse her yıl ((1+g)/(1+i))^t toplanır; kesirli yıl orantılı eklenir.
 */
export function progresifRantFaktor(nYears, artisPct, iskontoPct) {
  const n = Math.max(0, Number(nYears) || 0);
  if (n <= 0) return 0;
  const g = (Number(artisPct) || 0) / 100;
  const i = (Number(iskontoPct) || 0) / 100;
  if (Math.abs(g - i) < 1e-9) return n;
  const ratio = (1 + g) / (1 + i);
  let sum = 0;
  const whole = Math.floor(n);
  const frac = n - whole;
  for (let t = 1; t <= whole; t += 1) sum += Math.pow(ratio, t);
  if (frac > 0) sum += frac * Math.pow(ratio, whole + 1);
  return sum;
}

/**
 * Aktüeryal teknik faiz ile yıllık peşin değer katsayısı (basit yaklaşım).
 * Teknik faiz i için n yıl: Σ 1/(1+i)^t
 */
export function aktueryalFaktor(nYears, teknikFaizPct) {
  const n = Math.max(0, Number(nYears) || 0);
  const i = (Number(teknikFaizPct) || 0) / 100;
  if (n <= 0) return 0;
  if (Math.abs(i) < 1e-12) return n;
  let sum = 0;
  const whole = Math.floor(n);
  const frac = n - whole;
  for (let t = 1; t <= whole; t += 1) sum += 1 / Math.pow(1 + i, t);
  if (frac > 0) sum += frac / Math.pow(1 + i, whole + 1);
  return sum;
}

function futureFaktor(nYears, hesapYontemi, artisPct, iskontoPct, teknikFaizPct) {
  if (hesapYontemi === 'aktueryal') {
    // Artış=iskonto varsayımıyla peşin gelir; teknik faiz ile iskonto (PDF CSO örneği)
    // Pratikte birçok bilirkişi artış/iskonto eşitken yıllık×yıl kullanır.
    // Teknik faiz > 0 ve artış/iskonto ayrı verilmediyse teknik faizi kullan.
    const g = Number(artisPct) || 0;
    const d = Number(iskontoPct) || 0;
    if (Math.abs(g - d) < 1e-9 && Math.abs(g) > 1e-9) {
      return progresifRantFaktor(nYears, artisPct, iskontoPct);
    }
    return aktueryalFaktor(nYears, teknikFaizPct);
  }
  return progresifRantFaktor(nYears, artisPct, iskontoPct);
}

function fmtYil(y) {
  return Math.round((Number(y) || 0) * 100) / 100;
}

function round2(n) {
  return Math.round((Number(n) || 0) * 100) / 100;
}

/**
 * Dönem sürelerini hesapla (ortak).
 * takvimYili: true ise gelecek aktif/pasif, referans sistemdeki gibi
 * takvim yılı dilimlerine hizalanır (örn. aktif 2027–2043 = 17 yıl).
 */
export function hesaplaDonemler({
  olayTarihi,
  raporTarihi,
  dogumTarihi,
  cinsiyet = 'E',
  aktifBaslangicYas = 18,
  aktifBitisYas = 65,
  takvimYili = true,
}) {
  const olayYas = ageAtDate(dogumTarihi, olayTarihi);
  const raporYas = ageAtDate(dogumTarihi, raporTarihi);
  const bakiyeOmur = getBakiyeOmur(olayYas, cinsiyet);
  const yasamSonuYas = olayYas + bakiyeOmur;
  const yasamSonuTarih = addYears(olayTarihi, bakiyeOmur);
  const aktifBit = Number(aktifBitisYas) || 65;
  const emeklilikGunu = addYears(dogumTarihi, aktifBit);
  const aktifBitisTarih = emeklilikGunu ? addDays(emeklilikGunu, -1) : null;
  const aktifBas = Number(aktifBaslangicYas) || 18;

  const rapor = parseDate(raporTarihi);
  const olay = parseDate(olayTarihi);

  // Geçmiş: olay (veya sürekli başlangıç) → rapor tarihi (bilirkişi gibi; yıl sonuna uzatma yok)
  let gecmisGun;
  let gecmisYil;
  gecmisGun = daysBetween(olayTarihi, raporTarihi, true);
  gecmisYil = gecmisGun / DAYS_PER_YEAR;

  let gelecekAktifYil = 0;
  let gelecekPasifYil = 0;

  if (takvimYili && rapor && aktifBitisTarih && yasamSonuTarih) {
    // Aktif bitiş yaşının doğum gününe kadar; pasif sonrası
    const futureStart = addDays(raporTarihi, 1);
    if (futureStart && aktifBitisTarih.getTime() > futureStart.getTime()) {
      const aktifSon = aktifBitisTarih.getTime() <= yasamSonuTarih.getTime()
        ? aktifBitisTarih
        : yasamSonuTarih;
      gelecekAktifYil = Math.max(0, (aktifSon.getTime() - futureStart.getTime()) / (DAYS_PER_YEAR * MS_DAY));
      if (yasamSonuTarih.getTime() > aktifSon.getTime()) {
        gelecekPasifYil = (yasamSonuTarih.getTime() - aktifSon.getTime()) / (DAYS_PER_YEAR * MS_DAY);
      }
    } else if (futureStart && yasamSonuTarih.getTime() > futureStart.getTime()) {
      gelecekAktifYil = 0;
      gelecekPasifYil = (yasamSonuTarih.getTime() - futureStart.getTime()) / (DAYS_PER_YEAR * MS_DAY);
    }
    if (raporYas >= aktifBit) {
      gelecekAktifYil = 0;
      gelecekPasifYil = Math.max(0, yasamSonuYas - raporYas);
    }
  } else {
    const aktifSon = !yasamSonuTarih || !aktifBitisTarih
      ? null
      : (aktifBitisTarih.getTime() <= yasamSonuTarih.getTime() ? aktifBitisTarih : yasamSonuTarih);
    if (rapor && aktifSon && aktifSon.getTime() > rapor.getTime()) {
      gelecekAktifYil = (aktifSon.getTime() - rapor.getTime()) / (DAYS_PER_YEAR * MS_DAY);
    }
    if (rapor && yasamSonuTarih) {
      const pasifBas = aktifBitisTarih && aktifBitisTarih.getTime() > rapor.getTime()
        ? aktifBitisTarih
        : rapor;
      if (yasamSonuTarih.getTime() > pasifBas.getTime()) {
        gelecekPasifYil = (yasamSonuTarih.getTime() - pasifBas.getTime()) / (DAYS_PER_YEAR * MS_DAY);
      }
    }
    if (raporYas >= aktifBit) {
      gelecekAktifYil = 0;
      gelecekPasifYil = Math.max(0, yasamSonuYas - raporYas);
    }
  }

  const faalCalisma = Math.max(0, Math.min(aktifBit, yasamSonuYas) - Math.max(olayYas, aktifBas));

  return {
    olayYas: fmtYil(olayYas),
    raporYas: fmtYil(raporYas),
    bakiyeOmur: fmtYil(bakiyeOmur),
    yasamSonuYas: fmtYil(yasamSonuYas),
    faalCalisma: fmtYil(faalCalisma),
    gecmisGun,
    gecmisYil: fmtYil(gecmisYil),
    gelecekAktifYil: fmtYil(gelecekAktifYil),
    gelecekPasifYil: fmtYil(gelecekPasifYil),
    aktifBitisTarih,
    yasamSonuTarih,
  };
}

/**
 * İş gücü kaybı (maluliyet) — kazalının kendi zararı.
 * Progresif rant (bilirkişi uyumlu):
 * - Geçmiş: asgari dönem dilimleri veya sabit ücret × gün/30; GİÖ sonrası sürekli başlangıç
 * - Gelecek: yıl dilimli artış + iskonto katsayısı (1/(1+i)^t)
 * - Pasif: net asgari (BAM yaklaşımı) veya pasif aylık / oran
 */
export function hesaplaIsGucuKaybi(input) {
  const {
    olayTarihi,
    raporTarihi,
    dogumTarihi,
    cinsiyet = 'E',
    aylikUcret = null,
    davaliKusur = 100,
    isGucuKayip = 100,
    artisOrani = 10,
    iskontoOrani = 10,
    teknikFaiz = 0,
    hesapYontemi = 'progresif',
    aktifBaslangicYas = 18,
    aktifBitisYas = 65,
    pasifGelirOrani = 70,
    pasifAylikUcret = null,
    sigortaOdemesi = 0,
    geciciHesapla = false,
    geciciGun = 0,
    geciciOran = 100,
    bakiciHesapla = false,
    bakiciGun = 0,
    bakiciOran = 100,
    asgariNet = null,
    takvimYili = true,
    gelecegiRaporUcretineArtir = true,
  } = input || {};

  const olayAsgari = getAsgariNet(olayTarihi);
  const raporAsgari = getAsgariNet(raporTarihi);
  const guncelAsgari = getGuncelAsgariNet();
  const asgariForm = asgariNet != null && asgariNet !== '' ? Number(asgariNet) : null;
  const asgari = Math.max(0, asgariForm != null && !Number.isNaN(asgariForm) ? asgariForm : raporAsgari || guncelAsgari);

  const ucretGirilen = aylikUcret === '' || aylikUcret == null ? null : Number(aylikUcret);
  const useAsgariWage = ucretGirilen == null || Number.isNaN(ucretGirilen) || ucretGirilen <= 0;
  const ucret = useAsgariWage ? olayAsgari : Math.max(0, ucretGirilen);

  const kusur = Math.min(100, Math.max(0, Number(davaliKusur) || 0)) / 100;
  const kayip = Math.min(100, Math.max(0, Number(isGucuKayip) || 0)) / 100;
  const mahsup = Math.max(0, Number(sigortaOdemesi) || 0);
  const g = (Number(artisOrani) || 0) / 100;
  const iRate = (Number(iskontoOrani) || 0) / 100;

  const donem = hesaplaDonemler({
    olayTarihi,
    raporTarihi,
    dogumTarihi,
    cinsiyet,
    aktifBaslangicYas,
    aktifBitisYas,
    takvimYili,
  });

  const gunluk = round2(ucret / 30);
  const gGun = Math.max(0, Number(geciciGun) || 0);

  // Sürekli maluliyet geçmişi: GİÖ süresi varsa sonrasından başlar (bilirkişi: +3 ay → 20.09)
  let pastStart = parseDate(olayTarihi);
  if (geciciHesapla && gGun > 0) {
    const olayD = parseDate(olayTarihi);
    if (olayD && gGun % 30 === 0) {
      pastStart = new Date(olayD.getFullYear(), olayD.getMonth() + (gGun / 30), olayD.getDate());
    } else {
      pastStart = addDays(olayTarihi, gGun);
    }
  }
  const pastEnd = parseDate(raporTarihi);

  // --- Geçmiş ---
  let gecmisBrut = 0;
  let gecmisGunEff = 0;
  if (pastStart && pastEnd && pastEnd.getTime() >= pastStart.getTime()) {
    if (useAsgariWage) {
      const dilimler = asgariDonemDilimleri(pastStart, pastEnd);
      for (const d of dilimler) {
        gecmisGunEff += d.gun;
        gecmisBrut += d.gun * (d.aylikNet / 30) * kayip;
      }
    } else {
      gecmisGunEff = daysBetween(pastStart, pastEnd, true);
      // Ücreti dönem ortasına kadar artırmadan sabit olay ücreti (veya yıllık artırım)
      if (gelecegiRaporUcretineArtir && g !== 0 && pastStart && pastEnd) {
        const dilimler = calendarYearSegments(pastStart, pastEnd);
        const olayY = parseDate(olayTarihi)?.getFullYear() ?? pastStart.getFullYear();
        for (const seg of dilimler) {
          const aylik = ucret * Math.pow(1 + g, Math.max(0, seg.year - olayY));
          gecmisBrut += seg.gun * (aylik / 30) * kayip;
        }
      } else {
        gecmisBrut = gecmisGunEff * (ucret / 30) * kayip;
      }
    }
  }

  // --- Gelecek aktif / pasif (progresif yıl dilimleri) ---
  const futureStart = addDays(raporTarihi, 1);
  // Aktif dönem, emeklilik yaşının dolduğu günden bir gün önce biter (PDF: 30.06.2060, 65. yaş 01.07.2060)
  const emeklilikGunu = addYears(dogumTarihi, Number(aktifBitisYas) || 65);
  const aktifBitisTarih = emeklilikGunu ? addDays(emeklilikGunu, -1) : null;
  const yasamSonuTarih = donem.yasamSonuTarih;

  let gelecekAktifBrut = 0;
  let gelecekPasifBrut = 0;
  let ucretGelecek = ucret;
  let pasifAylik = asgari;

  const valuation = parseDate(raporTarihi);

  if (hesapYontemi === 'progresif' && futureStart && aktifBitisTarih && yasamSonuTarih && valuation) {
    const aktifSon = aktifBitisTarih.getTime() <= yasamSonuTarih.getTime() ? aktifBitisTarih : yasamSonuTarih;
    // Gelecek ilk aylık: rapor yılı asgari (veya ücret) × (1+g) — PDF: 28075.50×1.1
    const baseFutureMonthly = useAsgariWage
      ? raporAsgari * (1 + g)
      : ucret * Math.pow(1 + g, Math.max(0, valuation.getFullYear() - (parseDate(olayTarihi)?.getFullYear() || valuation.getFullYear())) + 1);
    ucretGelecek = baseFutureMonthly;

    if (futureStart.getTime() <= aktifSon.getTime()) {
      const aktifSegs = calendarYearSegments(futureStart, aktifSon);
      const firstYear = aktifSegs[0]?.year ?? valuation.getFullYear();
      for (const seg of aktifSegs) {
        const yearIndex = seg.year - firstYear; // 0,1,2...
        const aylik = baseFutureMonthly * Math.pow(1 + g, yearIndex);
        const yillik = aylik * 12 * (seg.ay / 12); // kısmi yıl
        const power = Math.max(1, seg.year - valuation.getFullYear() + 1);
        const disc = iRate === 0 ? 1 : 1 / Math.pow(1 + iRate, power);
        const pesin = yillik * disc;
        gelecekAktifBrut += pesin * kayip;
      }
    }

    const pasifBas = addDays(aktifSon, 1);
    if (pasifBas && yasamSonuTarih.getTime() >= pasifBas.getTime()) {
      // Pasif: net asgari (rapor) büyütülmüş; açık pasif aylık varsa onu kullan
      let pasifBase;
      if (pasifAylikUcret != null && pasifAylikUcret !== '') {
        pasifBase = Math.max(0, Number(pasifAylikUcret) || 0);
      } else if (useAsgariWage) {
        // BAM: pasif = net asgari (kesintisiz); gelecek için artırımlı
        pasifBase = raporAsgari * (1 + g);
      } else {
        const oranPasif = Math.max(0, Number(pasifGelirOrani) || 70) / 100;
        pasifBase = Math.max(asgari, baseFutureMonthly * oranPasif);
      }
      pasifAylik = pasifBase;
      const pasifSegs = calendarYearSegments(pasifBas, yasamSonuTarih);
      const firstAktifYear = (calendarYearSegments(futureStart, aktifSon)[0]?.year) ?? valuation.getFullYear();
      for (const seg of pasifSegs) {
        const yearIndex = seg.year - firstAktifYear;
        const aylik = pasifBase * Math.pow(1 + g, Math.max(0, yearIndex));
        const yillik = aylik * 12 * (seg.ay / 12);
        const power = Math.max(1, seg.year - valuation.getFullYear() + 1);
        const disc = iRate === 0 ? 1 : 1 / Math.pow(1 + iRate, power);
        gelecekPasifBrut += yillik * disc * kayip;
      }
    }
  } else {
    // Aktüeryal / eski basitleştirilmiş yol
    let ucretGecmis = ucret;
    ucretGelecek = ucret;
    if (gelecegiRaporUcretineArtir && g !== 0) {
      const olay = parseDate(olayTarihi);
      const rapor = parseDate(raporTarihi);
      if (olay && rapor) {
        const firstActiveYear = rapor.getFullYear() + 1;
        ucretGelecek = ucret * Math.pow(1 + g, Math.max(0, firstActiveYear - olay.getFullYear()) + 0.5);
      }
    }
    if (pasifAylikUcret != null && pasifAylikUcret !== '') {
      pasifAylik = Math.max(0, Number(pasifAylikUcret) || 0);
    } else {
      const oranPasif = Math.max(0, Number(pasifGelirOrani) || 70) / 100;
      pasifAylik = Math.max(asgari, ucretGelecek * oranPasif);
    }
    const yillikAktif = ucretGelecek * 12;
    const yillikPasif = pasifAylik * 12;
    const faktorAktif = futureFaktor(donem.gelecekAktifYil, hesapYontemi, artisOrani, iskontoOrani, teknikFaiz);
    const faktorPasif = futureFaktor(donem.gelecekPasifYil, hesapYontemi, artisOrani, iskontoOrani, teknikFaiz);
    gelecekAktifBrut = yillikAktif * faktorAktif * kayip;
    gelecekPasifBrut = yillikPasif * faktorPasif * kayip;
  }

  const gecmis = gecmisBrut * kusur;
  const gelecekAktif = gelecekAktifBrut * kusur;
  const gelecekPasif = gelecekPasifBrut * kusur;

  let geciciIsgoremezlik = 0;
  if (geciciHesapla) {
    const gOran = Math.min(100, Math.max(0, Number(geciciOran) || 0)) / 100;
    // GİÖ: olay tarihi ücreti / asgari dönem (PDF talep yoksa hesaplamaz; biz hesaplarsak)
    geciciIsgoremezlik = gGun * (ucret / 30) * gOran * kusur;
  }

  let bakiciGideri = 0;
  if (bakiciHesapla) {
    const bGun = Math.max(0, Number(bakiciGun) || 0);
    const bOran = Math.min(100, Math.max(0, Number(bakiciOran) || 0)) / 100;
    const bakiciGunluk = (useAsgariWage ? olayAsgari : asgari) / 30;
    bakiciGideri = bGun * bakiciGunluk * bOran * kusur;
  }

  const toplamBrut = gecmis + gelecekAktif + gelecekPasif + geciciIsgoremezlik + bakiciGideri;
  const toplam = Math.max(0, toplamBrut - mahsup);

  return {
    ...donem,
    gecmisGun: gecmisGunEff || donem.gecmisGun,
    gecmisYil: fmtYil((gecmisGunEff || donem.gecmisGun) / DAYS_PER_YEAR),
    gunlukUcret: round2(gunluk),
    ucretKullanilan: round2(ucret),
    useAsgariWage,
    olayAsgari: round2(olayAsgari),
    ucretGelecek: round2(ucretGelecek),
    yillikAktifKayip: round2(ucretGelecek * 12 * kayip),
    yillikPasifKayip: round2(pasifAylik * 12 * kayip),
    pasifAylikKullanilan: round2(pasifAylik),
    gecmisBrut: round2(gecmisBrut),
    gelecekAktifBrut: round2(gelecekAktifBrut),
    gelecekPasifBrut: round2(gelecekPasifBrut),
    gecmis: round2(gecmis),
    gelecekAktif: round2(gelecekAktif),
    gelecekPasif: round2(gelecekPasif),
    geciciIsgoremezlik: round2(geciciIsgoremezlik),
    bakiciGideri: round2(bakiciGideri),
    toplamBrut: round2(toplamBrut),
    sigortaMahsup: round2(mahsup),
    toplam: round2(toplam),
  };
}

/**
 * Destek payları — Yargıtay/AYİM: müteveffa 2, eş 2, her çocuk/ana/baba 1.
 * açıktaPayYok: müteveffa payı dağıtılmaz, paylar yeniden oranlanır (toplam 100).
 */
export function hesaplaDestekPaylari({
  esVar = false,
  cocukSayisi = 0,
  anneVar = false,
  babaVar = false,
  aciktaPayYok = false,
}) {
  const nCocuk = Math.max(0, Number(cocukSayisi) || 0);
  const self = aciktaPayYok ? 0 : 2;
  const es = esVar ? 2 : 0;
  const anne = anneVar ? 1 : 0;
  const baba = babaVar ? 1 : 0;
  const total = self + es + nCocuk + anne + baba;
  if (total <= 0) {
    return { total: 0, self: 0, es: 0, cocuk: 0, anne: 0, baba: 0 };
  }
  return {
    total,
    self: self / total,
    es: es / total,
    cocuk: nCocuk > 0 ? 1 / total : 0,
    anne: anne / total,
    baba: baba / total,
    nCocuk,
  };
}

function cocukDestekSonYas(cinsiyet, yuksekOgrenim, erkekSon = 18, kizSon = 22, uniSon = 25) {
  if (yuksekOgrenim) return Number(uniSon) || 25;
  const c = String(cinsiyet || 'E').toUpperCase();
  if (c === 'K') return Number(kizSon) || 22;
  return Number(erkekSon) || 18;
}

/**
 * Destekten yoksun kalma — hak sahiplerine göre dağılım.
 * Kazalı geliri dönem bazında hesaplanır; ardından pay × kusur; eşe evlenme indirimi.
 */
export function hesaplaDestekTazminati(input) {
  const {
    olayTarihi,
    raporTarihi,
    dogumTarihi,
    cinsiyet = 'E',
    aylikUcret = 0,
    ilaveAgi = 0,
    davaliKusur = 100,
    artisOrani = 10,
    iskontoOrani = 10,
    teknikFaiz = 0,
    hesapYontemi = 'progresif',
    aktifBaslangicYas = 18,
    aktifBitisYas = 60,
    pasifGelirOrani = 70,
    pasifAylikUcret = null,
    es = null, // { dogumTarihi, cinsiyet }
    cocuklar = [], // [{ dogumTarihi, cinsiyet, yuksekOgrenim }]
    anne = null,
    baba = null,
    aciktaPayYok = false,
    erkekDestekSon = 18,
    kizDestekSon = 22,
    uniDestekSon = 25,
    sgkPsd = 0,
  } = input || {};

  const ucret = Math.max(0, (Number(aylikUcret) || 0) + (Number(ilaveAgi) || 0));
  const kusur = Math.min(100, Math.max(0, Number(davaliKusur) || 0)) / 100;

  const donem = hesaplaDonemler({
    olayTarihi,
    raporTarihi,
    dogumTarihi,
    cinsiyet,
    aktifBaslangicYas,
    aktifBitisYas,
  });

  const gunluk = round2(ucret / 30);
  const yillikAktif = round2(ucret * 12);
  let pasifAylik = pasifAylikUcret != null && pasifAylikUcret !== ''
    ? Math.max(0, Number(pasifAylikUcret) || 0)
    : ucret * (Math.max(0, Number(pasifGelirOrani) || 70) / 100);
  if (pasifAylik < ucret * 0.7) pasifAylik = ucret * 0.7;
  const yillikPasif = pasifAylik * 12;

  const faktorAktif = futureFaktor(donem.gelecekAktifYil, hesapYontemi, artisOrani, iskontoOrani, teknikFaiz);
  const faktorPasif = futureFaktor(donem.gelecekPasifYil, hesapYontemi, artisOrani, iskontoOrani, teknikFaiz);

  const kazaliGecmis = donem.gecmisGun * gunluk;
  const kazaliAktif = yillikAktif * faktorAktif;
  const kazaliPasif = yillikPasif * faktorPasif;

  // Destek süreleri (rapor tarihinden)
  const kazaliDestekYil = donem.gelecekAktifYil + donem.gelecekPasifYil;
  const hakSahipleri = [];

  if (es && es.dogumTarihi) {
    const esBakiye = getBakiyeOmur(ageAtDate(es.dogumTarihi, olayTarihi), es.cinsiyet || 'K');
    const esDestek = Math.min(kazaliDestekYil + donem.gecmisYil, esBakiye);
    hakSahipleri.push({ tip: 'es', ad: 'Eş', destekYil: esDestek, ...es });
  }

  (cocuklar || []).forEach((c, idx) => {
    if (!c || !c.dogumTarihi) return;
    const sonYas = cocukDestekSonYas(c.cinsiyet, c.yuksekOgrenim, erkekDestekSon, kizDestekSon, uniDestekSon);
    const yasRapor = ageAtDate(c.dogumTarihi, raporTarihi);
    const kalan = Math.max(0, sonYas - yasRapor);
    const gecmisKalan = Math.max(0, sonYas - ageAtDate(c.dogumTarihi, olayTarihi));
    hakSahipleri.push({
      tip: 'cocuk',
      ad: `${idx + 1}. Çocuk`,
      destekYil: Math.min(kalan, kazaliDestekYil),
      destekYilGecmis: Math.min(gecmisKalan, donem.gecmisYil),
      index: idx,
      ...c,
    });
  });

  if (anne && anne.dogumTarihi) {
    const b = getBakiyeOmur(ageAtDate(anne.dogumTarihi, olayTarihi), anne.cinsiyet || 'K');
    hakSahipleri.push({ tip: 'anne', ad: 'Anne', destekYil: Math.min(b, kazaliDestekYil + donem.gecmisYil), ...anne });
  }
  if (baba && baba.dogumTarihi) {
    const b = getBakiyeOmur(ageAtDate(baba.dogumTarihi, olayTarihi), baba.cinsiyet || 'E');
    hakSahipleri.push({ tip: 'baba', ad: 'Baba', destekYil: Math.min(b, kazaliDestekYil + donem.gecmisYil), ...baba });
  }

  // Anlık pay (rapor tarihi itibarıyla hayatta/destek alanlar)
  const aktifCocuklar = (cocuklar || []).filter((c) => {
    if (!c?.dogumTarihi) return false;
    const sonYas = cocukDestekSonYas(c.cinsiyet, c.yuksekOgrenim, erkekDestekSon, kizDestekSon, uniDestekSon);
    return ageAtDate(c.dogumTarihi, raporTarihi) < sonYas;
  });

  const paylar = hesaplaDestekPaylari({
    esVar: !!(es && es.dogumTarihi),
    cocukSayisi: aktifCocuklar.length,
    anneVar: !!(anne && anne.dogumTarihi),
    babaVar: !!(baba && baba.dogumTarihi),
    aciktaPayYok,
  });

  // Basitleştirilmiş dönem payı: sabit pay oranları × dönem geliri × destek süresi oranı
  // (yıl yıl tablo yerine ağırlıklı ortalama — PDF toplamlarıyla uyumlu makro yöntem)
  const cocuk18alti = (cocuklar || []).filter((c) => c?.dogumTarihi && ageAtDate(c.dogumTarihi, raporTarihi) < 18).length;
  const esRaporYas = es?.dogumTarihi ? ageAtDate(es.dogumTarihi, raporTarihi) : 0;
  const evlenmePct = es?.dogumTarihi ? ayimEvlenmeIhtimali(esRaporYas, cocuk18alti) : 0;

  const dagilim = [];

  const pushKisi = (kisi, payOrani, gecmisCarpan = 1, aktifCarpan = 1, pasifCarpan = 1) => {
    const g = kazaliGecmis * payOrani * gecmisCarpan * kusur;
    let a = kazaliAktif * payOrani * aktifCarpan * kusur;
    let p = kazaliPasif * payOrani * pasifCarpan * kusur;
    let evlenmeIndirim = 0;
    if (kisi.tip === 'es' && evlenmePct > 0) {
      evlenmeIndirim = (a + p) * (evlenmePct / 100);
      a -= a * (evlenmePct / 100);
      p -= p * (evlenmePct / 100);
    }
    const sgk = Math.max(0, Number(sgkPsd) || 0) * payOrani;
    const toplam = Math.max(0, g + a + p - sgk);
    dagilim.push({
      tip: kisi.tip,
      ad: kisi.ad,
      payOrani: round2(payOrani * 100),
      gecmis: round2(g),
      gelecekAktif: round2(a),
      gelecekPasif: round2(p),
      evlenmeIndirim: round2(evlenmeIndirim),
      sgkPsd: round2(sgk),
      toplam: round2(toplam),
      destekYil: fmtYil(kisi.destekYil),
    });
  };

  if (es && es.dogumTarihi) {
    pushKisi(
      { tip: 'es', ad: 'Eş', destekYil: hakSahipleri.find((h) => h.tip === 'es')?.destekYil },
      paylar.es,
      1,
      1,
      1
    );
  }

  aktifCocuklar.forEach((c, idx) => {
    const sonYas = cocukDestekSonYas(c.cinsiyet, c.yuksekOgrenim, erkekDestekSon, kizDestekSon, uniDestekSon);
    const kalanAktif = Math.max(0, Math.min(donem.gelecekAktifYil, sonYas - ageAtDate(c.dogumTarihi, raporTarihi)));
    const kalanPasif = 0; // çocuklar genelde pasif döneme kalmaz
    const aktifCarpan = donem.gelecekAktifYil > 0 ? kalanAktif / donem.gelecekAktifYil : 0;
    const gecmisKalan = Math.max(0, Math.min(donem.gecmisYil, sonYas - ageAtDate(c.dogumTarihi, olayTarihi)));
    const gecmisCarpan = donem.gecmisYil > 0 ? gecmisKalan / donem.gecmisYil : 0;
    pushKisi(
      { tip: 'cocuk', ad: `${idx + 1}. Çocuk`, destekYil: kalanAktif },
      paylar.cocuk,
      gecmisCarpan,
      aktifCarpan,
      0
    );
  });

  if (anne && anne.dogumTarihi) {
    const h = hakSahipleri.find((x) => x.tip === 'anne');
    const oran = (donem.gecmisYil + kazaliDestekYil) > 0
      ? Math.min(1, (h?.destekYil || 0) / (donem.gecmisYil + kazaliDestekYil))
      : 1;
    pushKisi({ tip: 'anne', ad: 'Anne', destekYil: h?.destekYil }, paylar.anne, 1, oran, oran);
  }
  if (baba && baba.dogumTarihi) {
    const h = hakSahipleri.find((x) => x.tip === 'baba');
    const oran = (donem.gecmisYil + kazaliDestekYil) > 0
      ? Math.min(1, (h?.destekYil || 0) / (donem.gecmisYil + kazaliDestekYil))
      : 1;
    pushKisi({ tip: 'baba', ad: 'Baba', destekYil: h?.destekYil }, paylar.baba, 1, oran, oran);
  }

  const genelToplam = round2(dagilim.reduce((s, d) => s + d.toplam, 0));

  return {
    ...donem,
    gunlukUcret: round2(gunluk),
    yillikAktif: round2(yillikAktif),
    yillikPasif: round2(yillikPasif),
    kazaliGecmis: round2(kazaliGecmis),
    kazaliAktif: round2(kazaliAktif),
    kazaliPasif: round2(kazaliPasif),
    kazaliGecmisKusur: round2(kazaliGecmis * kusur),
    kazaliAktifKusur: round2(kazaliAktif * kusur),
    kazaliPasifKusur: round2(kazaliPasif * kusur),
    kazaliToplamKusur: round2((kazaliGecmis + kazaliAktif + kazaliPasif) * kusur),
    paylar,
    evlenmeIhtimali: evlenmePct,
    dagilim,
    genelToplam,
    // UI uyumu
    gecmis: round2(kazaliGecmis * kusur),
    gelecekAktif: round2(kazaliAktif * kusur),
    gelecekPasif: round2(kazaliPasif * kusur),
    toplam: genelToplam,
    sigortaMahsup: 0,
  };
}

export function formatTL(amount) {
  const n = Number(amount) || 0;
  return `${n.toLocaleString('tr-TR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })} TL`;
}

/** Form başlangıç değerleri (ayrı export — minify tree-shake hatasını önlemek için) */
export function createEmptyTazminatForm(today = null) {
  const d = today || (() => {
    const now = new Date();
    const m = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    return `${now.getFullYear()}-${m}-${day}`;
  })();
  return {
    mod: 'isgucu',
    kazaTuru: 'trafik',
    hesapYontemi: 'progresif',
    yasamTablosu: 'TRH-2010',
    teknikFaiz: '0',
    davaliKusur: '100',
    olayTarihi: '',
    dogumTarihi: '',
    cinsiyet: 'E',
    adSoyad: '',
    raporTarihi: d,
    aylikUcret: '',
    ilaveAgi: '0',
    isGucuKayip: '100',
    artisOrani: '10',
    iskontoOrani: '10',
    aktifBaslangicYas: '18',
    aktifBitisYas: '60',
    pasifGelirOrani: '70',
    pasifAylikUcret: '',
    sigortaOdemesi: '0',
    sgkPsd: '0',
    asgariNet: '',
    geciciHesapla: false,
    geciciGun: '',
    geciciOran: '100',
    bakiciHesapla: false,
    bakiciGun: '',
    bakiciOran: '100',
    bakiciTur: 'gun',
    bakiciUcret: 'brut',
    geliriYok: false,
    esPsd: '',
    annePsd: '',
    babaPsd: '',
    esVar: false,
    esDogum: '',
    esCinsiyet: 'K',
    anneVar: false,
    anneDogum: '',
    babaVar: false,
    babaDogum: '',
    cocuklar: [
      { dogumTarihi: '', cinsiyet: 'E', yuksekOgrenim: false },
    ],
  };
}
